import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';

// Base selectors
export const selectStudyYearCategories = (state: RootState) => state.studyYearCategories;
export const selectStudyYearCategoriesLoading = (state: RootState) =>
  state.studyYearCategories.isLoading;
export const selectStudyYearCategoriesError = (state: RootState) => state.studyYearCategories.error;
export const selectStudyYearCategoriesUpdating = (state: RootState) =>
  state.studyYearCategories.isUpdating;

// Individual data selectors
export const selectCurrentCategory = (state: RootState) =>
  state.studyYearCategories.currentCategory;
export const selectChildCategories = (state: RootState) =>
  state.studyYearCategories.childCategories;
export const selectCategoryData = (state: RootState) => state.studyYearCategories.categoryData;
export const selectCategoryCompanies = (state: RootState) =>
  state.studyYearCategories.categoryCompanies;

// NEW: Selectors for new endpoints
export const selectCategories = (state: RootState) => state.studyYearCategories.categories;

export const selectCategoryForecasts = (state: RootState) => state.studyYearCategories.categoryForecasts;

export const selectParentCategory = (state: RootState) => state.studyYearCategories.parentCategory;
export const selectCategoriesPagination = (state: RootState) =>
  state.studyYearCategories.pagination;
export const selectCategoriesFilters = (state: RootState) => state.studyYearCategories.filters;

export const selectCategoryChildData = (state: RootState) => state.studyYearCategories.childCategoryData;
//export const selectCategoryChildDataNew = (state: RootState) => state.studyYearCategories.childCategoryData;
// Count selectors
export const selectCategoryCompaniesCount = (state: RootState) =>
  state.studyYearCategories.categoryCompanies.length;
export const selectChildCategoriesCount = (state: RootState) =>
  state.studyYearCategories.childCategories.length;
export const selectCategoriesCount = (state: RootState) =>
  state.studyYearCategories.categories.length;
export const selectCategoryForecastsCount = (state: RootState) =>
  state.studyYearCategories.categoryForecasts.length;

// Computed selectors
export const selectCategoryDataWithForecasts = createSelector(
  [selectCategoryData],
  categoryData => {
    if (!categoryData) return null;

    return {
      ...categoryData,
      hasForecasts: categoryData.forecasts.length > 0,
      hasChildren: categoryData.children.length > 0,
    };
  }
);

// NEW: Operation status selectors
export const selectIsAnyStudyYearCategoryOperation = createSelector(
  [selectStudyYearCategoriesLoading, selectStudyYearCategoriesUpdating],
  (isLoading, isUpdating) => isLoading || isUpdating
);

// NEW: Categories with filtering and sorting
export const selectFilteredCategories = createSelector(
  [selectCategories, selectCategoriesFilters],
  (categories, filters) => {
    if (!filters || Object.keys(filters).length === 0) return categories;

    return categories.filter(category => {
      // Apply filters based on your filter criteria
      if (filters.year__eq && category.year !== filters.year__eq) return false;
      if (filters.is_segment__eq !== undefined && category.is_segment !== filters.is_segment__eq)
        return false;
      if (
        filters.category_name__like &&
        !category.category_name?.toLowerCase().includes(filters.category_name__like.toLowerCase())
      )
        return false;

      return true;
    });
  }
);

// NEW: Categories grouped by segment
export const selectCategoriesBySegment = createSelector([selectCategories], categories => {
  const segments = categories.filter(cat => cat.is_segment);
  const nonSegments = categories.filter(cat => !cat.is_segment);

  return {
    segments,
    categories: nonSegments,
    total: categories.length,
  };
});

// NEW: Category hierarchy selectors
export const selectCategoryHierarchy = createSelector(
  [selectCurrentCategory, selectChildCategories, selectParentCategory],
  (current, children, parent) => {
    if (!current) return null;

    return {
      current,
      parent,
      children,
      hasParent: !!parent,
      hasChildren: children.length > 0,
      hierarchyLevel: parent ? 1 : 0, // Simple level calculation
    };
  }
);

// NEW: Category financial metrics
export const selectCategoryFinancialMetrics = createSelector([selectCurrentCategory], category => {
  if (!category) return null;

  return {
    revenue: category.revenue,
    unitsold: category.units_sold,
    sellingPrice: category.selling_price,
    yoyRevenue: category.yoy_revenue,
    yoyRevenuePercentage: category.yoy_revenue_percentage,
    yoyUnitsPercentage: category.yoy_units_percentage,
  };
});

// NEW: Category forecasts analysis
export const selectCategoryForecastsAnalysis = createSelector(
  [selectCategoryForecasts],
  forecasts => {
    if (forecasts.length === 0) return null;

    const totalRevenue = forecasts.reduce((sum, forecast) => sum + forecast.revenue, 0);
    const averageGrowth =
      forecasts.reduce((sum, forecast) => sum + forecast.yoy_revenue_percentage, 0) /
      forecasts.length;

    return {
      totalForecasts: forecasts.length,
      totalRevenue,
      averageGrowth,
      forecastYears: forecasts.map(f => f.display_year).sort(),
    };
  }
);

// NEW: Category companies analysis
export const selectCategoryCompaniesAnalysis = createSelector(
  [selectCategoryCompanies],
  companies => {
    if (companies.length === 0) return null;

    const totalRevenue = companies.reduce((sum, company) => sum + company.revenue, 0);
    const topPerformer = companies.reduce((top, company) =>
      company.revenue > top.revenue ? company : top
    );

    return {
      totalCompanies: companies.length,
      totalRevenue,
      averageRevenue: totalRevenue / companies.length,
      topPerformer,
      companiesWithGrowth: companies.filter(c => c.yoy_revenue_percentage > 0).length,
    };
  }
);
